import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { StaffFormRow, StaffCredentialsModal } from '../components/staff';
import TeamSection from '../components/staff/TeamSection';
import DepartmentAssignment from '../components/staff/DepartmentAssignment';
import StaffDevices from '../components/staff/StaffDevices';
import useDepartmentStore from '../stores/departmentStore';
import { usePermissions } from '../context/PermissionContext';
import { ConfirmationModal } from '../components/common';
import { createEmptyStaffRow, STAFF_ROLES, STAFF_ROLE_LABELS, STAFF_STATUS_LABELS, STAFF_STATUS, SPECIALIZATION_LABELS, STAFF_ROLE_OPTIONS } from '../constants/staff';
import { validateStaffList, validateStaffRow } from '../utils/staffValidation';
import {
  fetchStaffList, bulkCreateStaff, updateStaff, disableStaff, enableStaff, resetStaffPassword,
  uploadStaffDocument, getStaffDocumentUrl, deleteStaffDocument,
  getDoctorSignature, updateDoctorSignature,
} from '../services/api/staffApi';
import { fetchDeviceSummary } from '../services/api/deviceApi';
import { ROUTES } from '../constants/routes';
import ChatLogo from '../components/common/ChatHeader/ChatLogo';
import ThemeToggleButton from '../components/common/ChatHeader/ThemeToggleButton';
import { UserProfilePanel } from '../components/common/UserProfile';
import './ManageStaffPage.css';

// ── Allowed file extensions (safe document & image types only) ──────
const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.rtf', '.odt', '.ods',
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg',
  '.heic', '.heif', '.tiff', '.tif',
];
const ALLOWED_ACCEPT = ALLOWED_EXTENSIONS.join(',');

function isFileAllowed(fileName) {
  if (!fileName) return false;
  const ext = '.' + fileName.split('.').pop().toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext);
}

/**
 * ManageStaffPage — Full-page staff management
 *
 * Features:
 * - Staff list table with status badges
 * - Inline add form (bulk create)
 * - Inline edit row
 * - Disable/Enable toggle with credential display
 * - Reset Password (generates new temp password, same username)
 *
 * Matches AdminDashboard styling with branded header and consistent layout.
 */
function ManageStaffPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // ── List state ────────────────────────────────────────────────────
  const [staffList, setStaffList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // ── Add form state ────────────────────────────────────────────────
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStaffRows, setNewStaffRows] = useState([createEmptyStaffRow()]);
  const [newRowErrors, setNewRowErrors] = useState([{}]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ── Edit state ────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  // Doctor Rx signature — hydrated separately via GET .../signature (its own
  // read/write surface, not part of the generic staff fields above).
  const [editSignatureLoading, setEditSignatureLoading] = useState(false);
  // Guards the async signature-hydration fetch in startEdit against a race:
  // if the admin cancels one doctor's edit and opens another's before the
  // first GET .../signature resolves, this stops the stale result from
  // landing on the wrong row.
  const activeEditIdRef = useRef(null);

  // ── Disable/Enable state ─────────────────────────────────────────
  const [togglingId, setTogglingId] = useState(null);

  // ── Reset Password state ─────────────────────────────────────────
  const [resettingId, setResettingId] = useState(null);

  // ── Credentials modal state ──────────────────────────────────────
  const [createdCredentials, setCreatedCredentials] = useState([]);
  const [showAddSuccess, setShowAddSuccess] = useState(false);

  // ── Confirm dialog state (replaces window.confirm) ───────────────
  const [confirmDialog, setConfirmDialog] = useState(null);
  // shape: { title, message, icon, variant, ctaLabel, onConfirm }

  // ── Error alert state (replaces alert()) ─────────────────────────
  const [errorAlert, setErrorAlert] = useState(null);
  // shape: { title, message }

  // ── Filter state ─────────────────────────────────────────────────
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // ── Document modal state ─────────────────────────────────────────
  const [docModalMember, setDocModalMember] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [docError, setDocError] = useState(null);
  const fileInputRef = useRef(null);

  // ── Phase 2.7 — Team modal state (admin-only inline team editor) ──
  const [teamModalDoctor, setTeamModalDoctor] = useState(null);
  const { role: viewerRole, hasPermission, memberId: viewerMemberId } = usePermissions();
  const isAdmin = (viewerRole || '').toUpperCase() === 'ADMIN';

  // ── A1 — Department assignment modal (admin + MANAGE_DEPARTMENT) ──
  const [deptModalMember, setDeptModalMember] = useState(null);
  const canManageDepartments = hasPermission('MANAGE_DEPARTMENT');

  // ── STAFF-DEVICE-LOCK / FE-4 — Devices modal + locked-count chip ──
  const [deviceModalMember, setDeviceModalMember] = useState(null);
  const canManageStaffDevices = hasPermission('MANAGE_STAFF');
  // { [member_id]: { locked, approved_count } } — single source of truth for
  // the row chip, seeded once from the summary endpoint and patched in place
  // by the modal's onChanged (never a second fetch, never a fake zero for a
  // member not yet in the map).
  const [deviceSummaryByMember, setDeviceSummaryByMember] = useState({});

  // R4/D2 — "is the signed-in admin the only admin in this org?" derived
  // entirely from the staff list Manage Staff already loaded above — no
  // dedicated endpoint, no extra fetch. Active (non-disabled) admins only;
  // a disabled admin can't sign in and can't rescue anyone either.
  const activeAdminCount = useMemo(
    () => staffList.filter(
      (s) => (s.role || '').toUpperCase() === STAFF_ROLES.ADMIN && s.status !== STAFF_STATUS.DISABLED
    ).length,
    [staffList]
  );
  const isSoleAdmin = activeAdminCount <= 1;

  // ── A1 — Department column (member→dept chips; single source of truth) ──
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);
  // ── Optional Department picker on the add/edit rows (single source of
  // truth: departmentStore). Fetched once, guarded by deptsLoaded — no storm.
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);

  // ── Theme-aware colors ───────────────────────────────────────────
  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';
  const headerBg = isDark ? 'rgba(14,14,15,0.85)' : 'rgba(255,255,255,0.85)';
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const headerShadow = isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.06)';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  // ── Fetch staff list ──────────────────────────────────────────────

  const loadStaff = useCallback(async () => {
    setIsLoading(true);
    setListError(null);

    const result = await fetchStaffList();
    if (result.success) {
      setStaffList(result.data);
    } else {
      setListError(result.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  // Populate the member→department map once for the Department column. Cost-aware:
  // one fetch per department (not per staff member). See departmentStore.hydrateAllMemberDepts.
  useEffect(() => {
    hydrateAllMemberDepts();
  }, [hydrateAllMemberDepts]);

  // Fetch the dept list ONCE for the add/edit Department picker. Guarded by
  // deptsLoaded (store dedups) with stable primitive deps — no request storm.
  useEffect(() => {
    if (!deptsLoaded) fetchDepartments();
  }, [deptsLoaded, fetchDepartments]);

  // ── STAFF-DEVICE-LOCK / FE-4 — one summary call per page load ─────────
  // Gated on the boolean permission result (stable primitive dep), never on
  // row count — this fires exactly once regardless of how many staff rows
  // render. A non-admin fires no device request at all. Failure just means
  // no chip; the page stays fully usable.
  useEffect(() => {
    if (!canManageStaffDevices) return;
    let cancelled = false;
    fetchDeviceSummary().then((result) => {
      if (cancelled || !result.success) return;
      const byMember = {};
      (result.data?.members || []).forEach((m) => {
        byMember[m.member_id] = { locked: m.locked, approved_count: m.approved_count };
      });
      setDeviceSummaryByMember(byMember);
    });
    return () => { cancelled = true; };
  }, [canManageStaffDevices]);

  // Patched from the Devices modal's onChanged after every successful
  // approve/remove — same shape the modal derives from, no re-fetch.
  const handleDeviceSummaryChanged = useCallback((summary) => {
    if (!summary?.member_id) return;
    setDeviceSummaryByMember((prev) => ({
      ...prev,
      [summary.member_id]: { locked: summary.locked, approved_count: summary.approved_count },
    }));
  }, []);

  // ── Add Staff ─────────────────────────────────────────────────────

  const openAddForm = () => {
    setShowAddForm(true);
    setNewStaffRows([createEmptyStaffRow()]);
    setNewRowErrors([{}]);
    setSubmitError(null);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setNewStaffRows([createEmptyStaffRow()]);
    setNewRowErrors([{}]);
    setSubmitError(null);
  };

  const addNewRow = () => {
    setNewStaffRows((prev) => [...prev, createEmptyStaffRow()]);
    setNewRowErrors((prev) => [...prev, {}]);
  };

  const removeNewRow = (index) => {
    if (newStaffRows.length <= 1) return;
    setNewStaffRows((prev) => prev.filter((_, i) => i !== index));
    setNewRowErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNewFieldChange = (index, field, value) => {
    setNewStaffRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setNewRowErrors((prev) => {
      const updated = [...prev];
      if (updated[index]?.[field]) {
        updated[index] = { ...updated[index] };
        delete updated[index][field];
      }
      return updated;
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const { rowErrors: errors, hasErrors } = validateStaffList(newStaffRows);
    setNewRowErrors(errors);
    if (hasErrors) return;

    setIsSubmitting(true);
    const result = await bulkCreateStaff(newStaffRows);
    setIsSubmitting(false);

    if (result.success) {
      const created = result.data?.created || [];
      // Only show credentials modal for staff with login accounts (not OTHER_STAFF)
      const withLogin = created.filter((c) => c.username && c.temporary_password);
      if (withLogin.length > 0) {
        setCreatedCredentials(withLogin);
      }
      closeAddForm();
      loadStaff();
    } else {
      setSubmitError(result.error);
    }
  };

  // ── Edit Staff ────────────────────────────────────────────────────

  const startEdit = (staff) => {
    const memberId = staff.member_id || staff.staff_id || staff.id;
    activeEditIdRef.current = memberId;
    setEditingId(memberId);
    setEditData({
      first_name: staff.first_name || '',
      last_name: staff.last_name || '',
      mobile: staff.mobile || '',
      email: staff.email || '',
      role: staff.role || 'DOCTOR',
      specialization: staff.specialization || '',
      registration_no: staff.registration_no || '',
      registration_number: staff.registration_number || '',
      registration_authority: staff.registration_authority || '',
      role_name: staff.role_name || '',
      signature_html: '',
      signature_font_family: null,
      signature_font_size: null,
    });
    setEditErrors({});

    // Hydrate the doctor's signature text separately — one GET call gives
    // the SignatureEditor the fetched text with no HTML parsing needed
    // anywhere on the frontend.
    if (staff.role === 'DOCTOR') {
      setEditSignatureLoading(true);
      getDoctorSignature(memberId).then((result) => {
        if (activeEditIdRef.current !== memberId) return; // stale — edit target changed
        if (result.success) {
          setEditData((prev) => (prev ? {
            ...prev,
            signature_html: result.data?.signature_html || '',
            signature_font_family: result.data?.font_family || null,
            signature_font_size: result.data?.font_size || null,
          } : prev));
        }
        setEditSignatureLoading(false);
      });
    }
  };

  const cancelEdit = () => {
    activeEditIdRef.current = null;
    setEditingId(null);
    setEditData(null);
    setEditErrors({});
    setEditSignatureLoading(false);
  };

  const handleEditFieldChange = (_, field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    setEditErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
  };

  const saveEdit = async () => {
    // signature_html is NOT part of the generic staff PUT —
    // StaffMemberUpdateRequest deliberately excludes it (extra="forbid")
    // so the signature's own role check can't be bypassed via this path.
    const { signature_html, signature_font_family, signature_font_size, ...staffFields } = editData;

    const errors = validateStaffRow(staffFields);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsUpdating(true);
    const result = await updateStaff(editingId, staffFields);

    if (!result.success) {
      setIsUpdating(false);
      setEditErrors({ _server: result.error });
      return;
    }

    // Sequential, not parallel: the signature save only happens once the
    // staff-fields save has actually succeeded, and its failure is reported
    // distinctly rather than as a blanket success/failure — the staff
    // details are already saved at this point regardless of what happens next.
    if (staffFields.role === 'DOCTOR') {
      const sigResult = await updateDoctorSignature(editingId, signature_html || '', {
        fontFamily: signature_font_family, fontSize: signature_font_size,
      });
      setIsUpdating(false);
      if (!sigResult.success) {
        setEditErrors({ _server: `Staff details saved, but the signature could not be saved: ${sigResult.error}` });
        return; // stay in edit mode so the admin can retry just the signature
      }
    } else {
      setIsUpdating(false);
    }

    cancelEdit();
    loadStaff();
  };

  // ── Disable / Enable Staff ──────────────────────────────────────

  const handleDisable = (staffId) => {
    setConfirmDialog({
      title: 'Disable Staff Member',
      message: 'This staff member will be logged out and cannot log in until re-enabled. Are you sure?',
      icon: '⚠️',
      variant: 'danger',
      ctaLabel: 'Disable',
      onConfirm: async () => {
        setConfirmDialog(null);
        setTogglingId(staffId);
        const result = await disableStaff(staffId);
        setTogglingId(null);

        if (result.success) {
          loadStaff();
        } else {
          setErrorAlert({ title: 'Failed to Disable', message: result.error });
        }
      },
    });
  };

  const handleEnable = async (staffId) => {
    setTogglingId(staffId);
    const result = await enableStaff(staffId);
    setTogglingId(null);

    if (result.success) {
      if (result.data?.temporary_password) {
        setCreatedCredentials([result.data]);
      }
      loadStaff();
    } else {
      setErrorAlert({ title: 'Failed to Enable', message: result.error });
    }
  };

  // ── Reset Password ───────────────────────────────────────────────

  const handleResetPassword = (staffId) => {
    setConfirmDialog({
      title: 'Reset Password',
      message: 'A new temporary password will be generated. The username stays the same. Are you sure?',
      icon: '🔑',
      variant: 'default',
      ctaLabel: 'Reset Password',
      onConfirm: async () => {
        setConfirmDialog(null);
        setResettingId(staffId);
        const result = await resetStaffPassword(staffId);
        setResettingId(null);

        if (result.success) {
          if (result.data?.temporary_password) {
            setCreatedCredentials([result.data]);
          }
        } else {
          setErrorAlert({ title: 'Failed to Reset Password', message: result.error });
        }
      },
    });
  };

  // ── Helpers ───────────────────────────────────────────────────────

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case STAFF_STATUS.ACTIVE:
        return { backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e' };
      case STAFF_STATUS.DISABLED:
        return { backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444' };
      case STAFF_STATUS.INACTIVE:
        return { backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444' };
      case STAFF_STATUS.PENDING:
        return { backgroundColor: 'rgba(234,179,8,0.15)', color: '#eab308' };
      default:
        return { backgroundColor: 'rgba(156,163,175,0.15)', color: '#9ca3af' };
    }
  };

  // ── Filtered staff list ────────────────────────────────────────────
  const filteredStaffList = staffList.filter((staff) => {
    if (filterRole && staff.role !== filterRole) return false;
    if (filterStatus && staff.status !== filterStatus) return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      const nameMatch = `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase().includes(q);
      const mobileMatch = (staff.mobile || '').includes(q);
      const emailMatch = (staff.email || '').toLowerCase().includes(q);
      const usernameMatch = (staff.username || '').toLowerCase().includes(q);
      if (!nameMatch && !mobileMatch && !emailMatch && !usernameMatch) return false;
    }
    return true;
  });

  // ── Documents handlers ─────────────────────────────────────────────
  const openDocModal = (staff) => {
    setDocModalMember(staff);
    setDocError(null);
  };

  const closeDocModal = () => {
    setDocModalMember(null);
    setDocError(null);
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isFileAllowed(file.name)) {
      setDocError(
        'File type not allowed. Accepted: PDF, DOC, XLS, PPT, images (JPG, PNG, etc.), TXT, CSV.'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setDocError('File too large (max 10 MB)');
      return;
    }

    const memberId = docModalMember?.member_id || docModalMember?.staff_id || docModalMember?.id;
    setIsUploading(true);
    setDocError(null);

    const result = await uploadStaffDocument(memberId, file);

    if (result.success) {
      // Refresh staff list to get updated documents array
      await loadStaff();
      // Update modal member with new data
      const refreshed = await fetchStaffList();
      if (refreshed.success) {
        const updated = refreshed.data.find((s) => (s.member_id || s.staff_id || s.id) === memberId);
        if (updated) setDocModalMember(updated);
      }
    } else {
      setDocError(result.error);
    }

    setIsUploading(false);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDocView = async (memberId, docIndex) => {
    const result = await getStaffDocumentUrl(memberId, docIndex);
    if (result.success && result.url) {
      window.open(result.url, '_blank', 'noopener');
    } else {
      setDocError(result.error || 'Failed to open document');
    }
  };

  const handleDocDelete = (memberId, docIndex, fileName) => {
    setConfirmDialog({
      title: 'Delete Document',
      message: `Are you sure you want to delete "${fileName}"?`,
      icon: '🗑️',
      variant: 'danger',
      ctaLabel: 'Delete',
      onConfirm: async () => {
        setConfirmDialog(null);
        const result = await deleteStaffDocument(memberId, docIndex);
        if (result.success) {
          await loadStaff();
          const refreshed = await fetchStaffList();
          if (refreshed.success) {
            const updated = refreshed.data.find((s) => (s.member_id || s.staff_id || s.id) === memberId);
            if (updated) setDocModalMember(updated);
            else closeDocModal();
          }
        } else {
          setDocError(result.error);
        }
      },
    });
  };

  // ── Render ────────────────────────────────────────────────────────

  // Permission gate (all hooks are called above; safe to early-return now)
  if (!hasPermission('MANAGE_STAFF')) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="manage-staff-page" style={{ background: bg }}>
      {/* ── Branded Header (matches AdminDashboard) ── */}
      <header
        className="admin-header"
        style={{
          background: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          boxShadow: headerShadow,
        }}
      >
        <div className="admin-header-inner">
          <div className="admin-header-left">
            <div className="logo-container">
              <ChatLogo isLoading={false} />
            </div>
            <button
              className="admin-back-btn"
              onClick={() => navigate(ROUTES.ORG)}
              style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                color: isDark ? '#5eead4' : '#0f766e',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                padding: '0.3rem 0.625rem',
                borderRadius: '0.5rem',
                border: '1px solid',
                fontSize: '0.75rem',
                cursor: 'pointer',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
              }}
            >
              ← Dashboard
            </button>
          </div>
          <div className="admin-header-right">
            <ThemeToggleButton />
            <UserProfilePanel />
          </div>
        </div>
      </header>

      {/* ── Page Content ── */}
      <main className="manage-staff-main">
        {/* Page Title + Add Button */}
        <div className="manage-staff-title-row">
          <div>
            <h1
              className="manage-staff-title"
              style={{ color: isDark ? '#f5f5f7' : '#111827' }}
            >
              👥 Manage Staff
            </h1>
            <p
              className="manage-staff-subtitle"
              style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
            >
              Add, edit, and manage your team members
            </p>
          </div>
          <button
            className="manage-staff-add-btn"
            onClick={openAddForm}
            disabled={showAddForm}
          >
            + Add Staff
          </button>
        </div>

        {/* ── Filter Bar ── */}
        <div
          className="manage-staff-filter-bar"
          style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
          }}
        >
          <div className="manage-staff-filter-group">
            <label style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>Role</label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="manage-staff-filter-select"
              style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                color: isDark ? '#e8e9ed' : '#111827',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              }}
            >
              <option value="">All Roles</option>
              {STAFF_ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="manage-staff-filter-group">
            <label style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="manage-staff-filter-select"
              style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                color: isDark ? '#e8e9ed' : '#111827',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              }}
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="DISABLED">Disabled</option>
            </select>
          </div>

          <div className="manage-staff-filter-group manage-staff-filter-search-group">
            <label style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>Search</label>
            <input
              type="text"
              placeholder="Name, mobile, email..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="manage-staff-filter-input"
              style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                color: isDark ? '#e8e9ed' : '#111827',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              }}
            />
          </div>

          {(filterRole || filterStatus || filterSearch) && (
            <button
              className="manage-staff-filter-clear"
              onClick={() => { setFilterRole(''); setFilterStatus(''); setFilterSearch(''); }}
              style={{ color: isDark ? '#5eead4' : '#0f766e' }}
            >
              Clear filters
            </button>
          )}

          <span
            className="manage-staff-filter-count"
            style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
          >
            {filteredStaffList.length} of {staffList.length}
          </span>
        </div>

        {/* Add Form (inline expandable) */}
        {showAddForm && (
          <div
            className="manage-staff-card"
            style={{
              background: cardBg,
              border: `1px solid ${cardBorder}`,
            }}
          >
            <div className="manage-staff-card-header">
              <h2 style={{ color: isDark ? '#f5f5f7' : '#111827' }}>Add New Staff</h2>
              <button
                className="manage-staff-close-btn"
                onClick={closeAddForm}
                style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
              >
                ✕
              </button>
            </div>

            {submitError && (
              <div className="manage-staff-error">
                <span>⚠️</span>
                <p>{submitError}</p>
              </div>
            )}

            <form onSubmit={handleAddSubmit} noValidate>
              {newStaffRows.map((row, idx) => (
                <StaffFormRow
                  key={row._key}
                  data={row}
                  errors={newRowErrors[idx] || {}}
                  index={idx}
                  onChange={handleNewFieldChange}
                  onRemove={removeNewRow}
                  disabled={isSubmitting}
                  showRemove={newStaffRows.length > 1}
                  departments={departments}
                />
              ))}

              <button
                type="button"
                className="manage-staff-add-another"
                onClick={addNewRow}
                disabled={isSubmitting}
              >
                <span>+</span> Add Another
              </button>

              <div className="manage-staff-form-actions">
                <button
                  type="button"
                  className="manage-staff-btn-secondary"
                  onClick={closeAddForm}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="manage-staff-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Staff'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="manage-staff-loading" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
            <div className="manage-staff-spinner" />
            Loading staff...
          </div>
        )}

        {/* Error */}
        {listError && (
          <div className="manage-staff-error">
            <span>⚠️</span>
            <p>{listError}</p>
          </div>
        )}

        {/* Staff Table */}
        {!isLoading && !listError && (
          <>
            {staffList.length === 0 ? (
              <div
                className="manage-staff-empty"
                style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  color: isDark ? '#9ca3af' : '#6b7280',
                }}
              >
                <span style={{ fontSize: '2.5rem' }}>👥</span>
                <h3 style={{ color: isDark ? '#e8e9ed' : '#111827' }}>No Staff Members</h3>
                <p>Click "+ Add Staff" to add your first team member.</p>
              </div>
            ) : (
              <div
                className="manage-staff-table-card"
                style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                }}
              >
                <div className="manage-staff-table-wrap">
                  <table className="manage-staff-table">
                    <thead>
                      <tr>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Name</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Role</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Specialization</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Mobile</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Email</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Department</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Status</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Username</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Docs</th>
                        <th style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaffList.map((staff) => {
                        const memberId = staff.member_id || staff.staff_id || staff.id;
                        const isEditing = editingId === memberId;

                        if (isEditing) {
                          return (
                            <tr key={memberId} className="manage-staff-edit-row">
                              <td colSpan={10}>
                                <div className="manage-staff-edit-card">
                                  <StaffFormRow
                                    data={editData}
                                    errors={editErrors}
                                    index={0}
                                    onChange={handleEditFieldChange}
                                    showRemove={false}
                                    disabled={isUpdating}
                                    departments={departments}
                                    memberId={memberId}
                                    signatureLoading={editSignatureLoading}
                                  />
                                  {editErrors._server && (
                                    <p className="staff-field-error" style={{ marginTop: 8 }}>
                                      {editErrors._server}
                                    </p>
                                  )}
                                  <div className="manage-staff-edit-actions">
                                    <button
                                      className="manage-staff-btn-secondary"
                                      onClick={cancelEdit}
                                      disabled={isUpdating}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className="manage-staff-btn-primary"
                                      onClick={saveEdit}
                                      disabled={isUpdating}
                                    >
                                      {isUpdating ? 'Saving...' : 'Save'}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr
                            key={memberId}
                            style={{
                              borderColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                            }}
                          >
                            <td style={{ color: isDark ? '#e8e9ed' : '#111827', fontWeight: 500 }}>
                              {staff.first_name} {staff.last_name}
                            </td>
                            <td style={{ color: isDark ? '#d1d5db' : '#374151' }}>
                              {STAFF_ROLE_LABELS[staff.role] || staff.role}
                            </td>
                            <td style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.8125rem' }}>
                              {staff.role === 'DOCTOR' && staff.specialization
                                ? (SPECIALIZATION_LABELS[staff.specialization] || staff.specialization)
                                : staff.role === 'OTHER_STAFF' && staff.role_name
                                  ? staff.role_name
                                  : staff.role === 'LAB_TECHNICIAN' && staff.registration_number
                                    ? `${staff.registration_number}${staff.registration_authority ? ` · ${staff.registration_authority}` : ''}`
                                    : '—'}
                            </td>
                            <td style={{ color: isDark ? '#d1d5db' : '#374151' }}>{staff.mobile}</td>
                            <td style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{staff.email || '—'}</td>
                            <td>
                              <div className="flex flex-wrap gap-1" style={{ maxWidth: 220 }}>
                                {(deptsByMember[memberId] || []).length > 0 ? (
                                  (deptsByMember[memberId] || []).map((d) => (
                                    <span
                                      key={d.dept_id}
                                      className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-semibold bg-teal-50 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700 text-teal-900 dark:text-teal-100"
                                    >
                                      {d.name || d.dept_id}
                                    </span>
                                  ))
                                ) : (
                                  <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>—</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span
                                className="manage-staff-status-badge"
                                style={getStatusBadgeStyle(staff.status)}
                              >
                                {STAFF_STATUS_LABELS[staff.status] || staff.status || 'Active'}
                              </span>
                            </td>
                            <td style={{ color: isDark ? '#9ca3af' : '#6b7280', fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                              {staff.role === 'OTHER_STAFF' ? 'N/A' : (staff.username || '—')}
                            </td>
                            <td>
                              <button
                                className={`manage-staff-docs-btn${(staff.documents || []).length === 0 ? ' manage-staff-docs-btn--empty' : ''}`}
                                onClick={() => openDocModal(staff)}
                                title={(staff.documents || []).length === 0 ? 'No documents — click to upload' : 'View / Upload documents'}
                              >
                                <span className="manage-staff-docs-btn-icon">
                                  {(staff.documents || []).length === 0 ? '⚠️' : '📄'}
                                </span>
                                <span className="manage-staff-docs-btn-label">
                                  {(staff.documents || []).length > 0
                                    ? `${(staff.documents || []).length} Doc${(staff.documents || []).length > 1 ? 's' : ''}`
                                    : 'No Docs'}
                                </span>
                              </button>
                            </td>
                            <td>
                              <div className="manage-staff-row-actions">
                                {/* Edit */}
                                <button
                                  className="manage-staff-action-btn manage-staff-action-edit"
                                  onClick={() => startEdit(staff)}
                                  disabled={staff.status === 'DISABLED'}
                                  title={staff.status === 'DISABLED' ? 'Enable staff first to edit' : 'Edit'}
                                >
                                  Edit
                                </button>

                                {/* Phase 2.7 — Team (admin-only, doctor rows) */}
                                {isAdmin && staff.role === 'DOCTOR' && staff.status !== 'DISABLED' && (
                                  <button
                                    className="manage-staff-action-btn manage-staff-action-edit"
                                    onClick={() => setTeamModalDoctor(staff)}
                                    title="Configure this doctor's team. Members listed here may be assigned to plan steps."
                                    style={{ background: isDark ? '#1e1b4b' : '#eef2ff', color: isDark ? '#e0e7ff' : '#3730a3', borderColor: isDark ? '#4338ca' : '#a5b4fc' }}
                                  >
                                    Team
                                  </button>
                                )}

                                {/* A1 — Departments (admin + MANAGE_DEPARTMENT) */}
                                {canManageDepartments && staff.status !== 'DISABLED' && (
                                  <button
                                    className="manage-staff-action-btn manage-staff-action-edit"
                                    onClick={() => setDeptModalMember(staff)}
                                    title="Assign this staff member to one or more departments."
                                    style={{ background: isDark ? '#134e4a' : '#ccfbf1', color: isDark ? '#99f6e4' : '#0f766e', borderColor: isDark ? '#0f766e' : '#5eead4' }}
                                  >
                                    Departments
                                  </button>
                                )}

                                {/* STAFF-DEVICE-LOCK / FE-4 — Devices (admin + MANAGE_STAFF) */}
                                {canManageStaffDevices && staff.role !== 'OTHER_STAFF' && staff.status !== 'DISABLED' && (
                                  <button
                                    className="manage-staff-action-btn manage-staff-action-edit"
                                    onClick={() => setDeviceModalMember(staff)}
                                    title="Manage which devices this member can sign in from."
                                    style={{ background: isDark ? '#1e293b' : '#eff6ff', color: isDark ? '#93c5fd' : '#1d4ed8', borderColor: isDark ? '#3b82f6' : '#93c5fd' }}
                                  >
                                    Devices
                                    {deviceSummaryByMember[memberId]?.locked && (
                                      <span
                                        className="manage-staff-device-chip"
                                        title="Approved devices"
                                        style={{
                                          marginLeft: 6,
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                          padding: '0 5px',
                                          borderRadius: 999,
                                          background: isDark ? '#1e40af' : '#bfdbfe',
                                          color: isDark ? '#dbeafe' : '#1e3a8a',
                                        }}
                                      >
                                        {deviceSummaryByMember[memberId].approved_count}
                                      </span>
                                    )}
                                  </button>
                                )}

                                {/* Reset Password — not for OTHER_STAFF (no login) */}
                                {staff.status !== 'DISABLED' && staff.role !== 'OTHER_STAFF' && (
                                  <button
                                    className="manage-staff-action-btn manage-staff-action-reset"
                                    onClick={() => handleResetPassword(memberId)}
                                    disabled={resettingId === memberId}
                                    title="Reset password — generates new temporary password"
                                  >
                                    {resettingId === memberId ? 'Resetting...' : 'Reset Password'}
                                  </button>
                                )}

                                {/* Disable / Enable */}
                                {staff.status === 'DISABLED' ? (
                                  <button
                                    className="manage-staff-action-btn manage-staff-action-enable"
                                    onClick={() => handleEnable(memberId)}
                                    disabled={togglingId === memberId}
                                  >
                                    {togglingId === memberId ? 'Enabling...' : 'Enable'}
                                  </button>
                                ) : (
                                  <button
                                    className="manage-staff-action-btn manage-staff-action-disable"
                                    onClick={() => handleDisable(memberId)}
                                    disabled={togglingId === memberId || staff.role === 'ADMIN'}
                                    title={staff.role === 'ADMIN' ? 'Admin cannot be disabled' : 'Disable'}
                                  >
                                    {togglingId === memberId ? 'Disabling...' : 'Disable'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Credentials modal (after create / enable / reset password) */}
      <StaffCredentialsModal
        isOpen={createdCredentials.length > 0}
        credentials={createdCredentials}
        onClose={() => setCreatedCredentials([])}
      />

      {/* Add success confirmation */}
      <ConfirmationModal
        isOpen={showAddSuccess}
        title="Staff Added Successfully"
        message="New staff members have been added to your organisation."
        ctaLabel="Done"
        icon="✅"
        onConfirm={() => setShowAddSuccess(false)}
      />

      {/* Confirm dialog (replaces window.confirm) */}
      {confirmDialog && (
        <ConfirmationModal
          isOpen={true}
          title={confirmDialog.title}
          message={confirmDialog.message}
          icon={confirmDialog.icon}
          variant={confirmDialog.variant}
          ctaLabel={confirmDialog.ctaLabel}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* Error alert (replaces alert()) */}
      {errorAlert && (
        <ConfirmationModal
          isOpen={true}
          title={errorAlert.title}
          message={errorAlert.message}
          icon="❌"
          ctaLabel="OK"
          onConfirm={() => setErrorAlert(null)}
        />
      )}

      {/* ── Document Modal ── */}
      {docModalMember && (
        <div className="manage-staff-doc-overlay" onClick={closeDocModal}>
          <div
            className="manage-staff-doc-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: isDark ? '#1a1a2e' : '#fff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            }}
          >
            <div className="manage-staff-doc-modal-header">
              <h3 style={{ color: isDark ? '#f5f5f7' : '#111827', margin: 0 }}>
                📄 Documents — {docModalMember.first_name} {docModalMember.last_name}
              </h3>
              <button
                className="manage-staff-close-btn"
                onClick={closeDocModal}
                style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
              >
                ✕
              </button>
            </div>

            {docError && (
              <div className="manage-staff-error" style={{ margin: '0.75rem 0 0' }}>
                <span>⚠️</span>
                <p>{docError}</p>
              </div>
            )}

            <div className="manage-staff-doc-upload-row">
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_ACCEPT}
                onChange={handleDocUpload}
                disabled={isUploading}
                style={{ display: 'none' }}
                id="doc-upload-input"
              />
              <button
                className="manage-staff-doc-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <><span className="manage-staff-spinner" style={{ width: 14, height: 14 }} /> Uploading...</>
                ) : (
                  <><span style={{ fontSize: '1.1rem' }}>⬆️</span> Upload Document</>
                )}
              </button>
              <span style={{ color: isDark ? '#6b7280' : '#9ca3af', fontSize: '0.725rem', lineHeight: 1.3 }}>
                Max 10 MB • PDF, DOC, XLS, images accepted<br/>
                Files are compressed automatically
              </span>
            </div>

            <div className="manage-staff-doc-list">
              {(docModalMember.documents || []).length === 0 ? (
                <p style={{ color: isDark ? '#6b7280' : '#9ca3af', textAlign: 'center', padding: '2rem 0', margin: 0, fontSize: '0.875rem' }}>
                  No documents uploaded yet
                </p>
              ) : (
                (docModalMember.documents || []).map((doc, idx) => (
                  <div
                    key={idx}
                    className="manage-staff-doc-item"
                    style={{
                      borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    }}
                  >
                    <div className="manage-staff-doc-info">
                      <span
                        className="manage-staff-doc-name"
                        style={{ color: isDark ? '#e8e9ed' : '#111827' }}
                      >
                        {doc.file_name}
                      </span>
                      <span style={{ color: isDark ? '#6b7280' : '#9ca3af', fontSize: '0.75rem' }}>
                        {doc.size_bytes ? `${(doc.size_bytes / 1024).toFixed(1)} KB` : ''}
                        {doc.uploaded_at ? ` • ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}
                      </span>
                    </div>
                    <div className="manage-staff-doc-actions">
                      <button
                        className="manage-staff-action-btn manage-staff-action-edit"
                        onClick={() => handleDocView(
                          docModalMember.member_id || docModalMember.staff_id || docModalMember.id,
                          idx
                        )}
                        title="View / Download"
                      >
                        View
                      </button>
                      <button
                        className="manage-staff-action-btn manage-staff-action-disable"
                        onClick={() => handleDocDelete(
                          docModalMember.member_id || docModalMember.staff_id || docModalMember.id,
                          idx,
                          doc.file_name
                        )}
                        title="Delete document"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phase 2.7 — Team modal (admin-only inline editor) */}
      <TeamSection
        open={!!teamModalDoctor}
        onClose={() => setTeamModalDoctor(null)}
        doctor={teamModalDoctor}
        staff={(staffList || []).map((s) => ({
          member_id: s.member_id,
          role: (s.role || '').toUpperCase(),
          name: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || s.member_id,
          first_name: s.first_name,
          last_name: s.last_name,
        }))}
        isAdmin={isAdmin}
      />

      {/* A1 — Department assignment modal (admin + MANAGE_DEPARTMENT) */}
      <DepartmentAssignment
        open={!!deptModalMember}
        onClose={() => setDeptModalMember(null)}
        member={deptModalMember && {
          member_id: deptModalMember.member_id || deptModalMember.staff_id || deptModalMember.id,
          first_name: deptModalMember.first_name,
          last_name: deptModalMember.last_name,
          role: (deptModalMember.role || '').toUpperCase(),
        }}
        isAdmin={canManageDepartments}
      />

      {/* STAFF-DEVICE-LOCK / FE-4 — Devices modal (admin + MANAGE_STAFF) */}
      <StaffDevices
        isOpen={!!deviceModalMember}
        onClose={() => setDeviceModalMember(null)}
        member={deviceModalMember && {
          member_id: deviceModalMember.member_id || deviceModalMember.staff_id || deviceModalMember.id,
          first_name: deviceModalMember.first_name,
          last_name: deviceModalMember.last_name,
        }}
        onChanged={handleDeviceSummaryChanged}
        // R4/D2+D3 — sole-admin lockout guards. Both derived client-side
        // (viewer session + the staff list already loaded), no new fetch.
        isSelf={!!(deviceModalMember && viewerMemberId
          && (deviceModalMember.member_id || deviceModalMember.staff_id || deviceModalMember.id) === viewerMemberId)}
        isSoleAdmin={isSoleAdmin}
      />
    </div>
  );
}

export default ManageStaffPage;
